import c from 'classnames';
import {
  TextColor,
  isSemanticColor,
  type RampColorToken,
  type TextColorType,
} from '../../helpers/colorTokens';
import styles from './Typography.module.css';

export type TypographyComponentVariant =
  | 'display-l'
  | 'display-m'
  | 'display-s'
  | 'heading-xl'
  | 'heading-l'
  | 'heading-m'
  | 'heading-s'
  | 'subheading'
  | 'eyebrow'
  | 'caption'
  | 'label'
  | 'legal'
  | 'body-s'
  | 'paragraph'
  | 'body-m'
  | 'chart-label';

type TextVariantClass =
  | 'displayL'
  | 'displayM'
  | 'displayS'
  | 'headingXL'
  | 'headingL'
  | 'headingM'
  | 'headingS'
  | 'subheading'
  | 'eyebrow'
  | 'caption'
  | 'label'
  | 'legal'
  | 'bodyS'
  | 'paragraph'
  | 'bodyM'
  | 'chartLabel';

const variantMap: {
  [key in TypographyComponentVariant]: TextVariantClass;
} = {
  'display-l': 'displayL',
  'display-m': 'displayM',
  'display-s': 'displayS',
  'heading-xl': 'headingXL',
  'heading-l': 'headingL',
  'heading-m': 'headingM',
  'heading-s': 'headingS',
  subheading: 'subheading',
  eyebrow: 'eyebrow',
  caption: 'caption',
  label: 'label',
  legal: 'legal',
  'body-s': 'bodyS',
  paragraph: 'paragraph',
  'body-m': 'bodyM',
  'chart-label': 'chartLabel',
};

/**
 * `regular` and `semibold` arrived with pdf-report's fork; `heavy` is a
 * long-standing alias of `bold`. Each resolves to an app-overridable token,
 * which is the point — `medium` is 600 in the portals and 500 in the PDF.
 */
export type TextWeight =
  | 'light'
  | 'regular'
  | 'medium'
  | 'semibold'
  | 'bold'
  | 'heavy';

/**
 * Fixed print sizes, from pdf-report's `.f1`–`.f10`.
 *
 * @deprecated Use `variant`. These exist only so pdf-report's forked Typography
 * could be deleted without rewriting 457 call sites, and are deprecated from
 * the day they landed so the convergence stays visible.
 *
 * `f1` → `display-s` and `f2` → `heading-xl` are exact swaps. `f3` is 23px
 * against `heading-l`'s 24px. `f4`–`f10` (18px–6px) sit at or below the screen
 * scale's 13px floor, so closing that gap is a design decision about whether
 * print gets its own named variants.
 */
export type TypographyPrintSize =
  | 'f1'
  | 'f2'
  | 'f3'
  | 'f4'
  | 'f5'
  | 'f6'
  | 'f7'
  | 'f8'
  | 'f9'
  | 'f10';

/* Class KEYS, not resolved class names — these maps must not dereference
 * `styles` at module scope. ops-dashboard pulls this module in via ui's barrel
 * without ever rendering Typography, and there the CSS-module binding is not
 * available: a module-level `styles.print1` throws `ReferenceError: styles is
 * not defined` on import and takes the app's entry point down. `styles` was
 * previously only touched inside render, which is why this was latent. Resolve
 * at render time to keep it that way. */
const printSizeClass: Record<TypographyPrintSize, string> = {
  f1: 'print1',
  f2: 'print2',
  f3: 'print3',
  f4: 'print4',
  f5: 'print5',
  f6: 'print6',
  f7: 'print7',
  f8: 'print8',
  f9: 'print9',
  f10: 'print10',
};

/* Also keys, for the same reason as printSizeClass above. */
const weightClass: Record<TextWeight, string | undefined> = {
  // The resting weight the base class already sets, so: no class.
  regular: undefined,
  light: 'light',
  medium: 'medium',
  semibold: 'semibold',
  bold: 'bold',
  heavy: 'heavy',
};

/** The element Typography renders. Defaults to `span`, as it always has. */
export type TypographyElement =
  | 'span'
  | 'p'
  | 'div'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6';

export const getTextColorFromEvaluationColor = (color: string) => {
  switch (color) {
    case 'green':
      return TextColor.GREEN;
    case 'orange':
      return TextColor.ORANGE;
    case 'red':
      return TextColor.RED;
    case 'grey':
      return TextColor.GREY;
  }
  return TextColor.DARK;
};

type TypographyBaseProps = Omit<
  React.ComponentPropsWithoutRef<'span'>,
  'color'
> & {
  medium?: boolean;
  light?: boolean;
  /**
   * A semantic name (preferred), or a raw ramp token for steps the semantic
   * names cannot reach. Semantic names apply a class, so callers can still
   * out-specify them; a raw token goes inline and they cannot.
   */
  color?: TextColorType | RampColorToken;
  italic?: boolean;
  className?: string;
  weight?: TextWeight;
  /** Tracking. pdf-report called this `leading`, which it is not. */
  tracking?: 'normal' | 'tight' | 'tighter';
  underline?: boolean;
  as?: TypographyElement;
  children: React.ReactNode;
};

/**
 * `variant` and `size` are mutually exclusive — a responsive screen variant or
 * a fixed print size. Requiring exactly one keeps a single component honest
 * about serving two scales.
 */
export type TypographyProps = TypographyBaseProps &
  (
    | { variant: TypographyComponentVariant; size?: never }
    | {
        /** @deprecated Use `variant`. See {@link TypographyPrintSize}. */
        size: TypographyPrintSize;
        variant?: never;
      }
  );

export const Typography = ({
  variant,
  size,
  medium,
  light,
  color,
  italic,
  className,
  weight,
  tracking,
  underline,
  as: Component = 'span',
  style,
  children,
  ...props
}: TypographyProps) => {
  // Ramp tokens go inline, but before `style`, so a caller's own colour wins.
  const semanticColor = color && isSemanticColor(color) ? color : undefined;
  const rampColor = color && !semanticColor ? color : undefined;

  return (
    <Component
      className={c(
        className,
        styles.typography,
        variant ? styles[variantMap[variant]] : undefined,
        size ? styles[printSizeClass[size]] : undefined,
        italic && styles.italic,
        semanticColor && styles[semanticColor],
        weight && weightClass[weight]
          ? styles[weightClass[weight]!]
          : undefined,
        medium && styles.medium,
        light && styles.light,
        tracking && tracking !== 'normal' ? styles[tracking] : undefined,
        underline && styles.underline,
      )}
      style={rampColor ? { color: `var(--${rampColor})`, ...style } : style}
      {...props}
    >
      {children}
    </Component>
  );
};

export default Typography;
